import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../Modal';
import { ClientModal } from '../ClientModal';
import { QuoteModal } from '../QuoteModal';
import { JobCosts } from '../JobCosts';
import Lightbox from '../Lightbox/Lightbox';
import { staffService, servicesService, clientsService, appointmentsService, imagesService, quotesService, jobsService } from '../../services/api';
import { brisbaneInputsToUtc, utcToBrisbaneInputs } from '../../utils/dateFormatters';
import { addMinutesToTime, minutesBetween, fmtHours, DEFAULT_VISIT_MINUTES } from '../../utils/schedule';
import { priceLabel, formatMoney } from '../../utils/pricing';
import './AppointmentModal.css';

const STATUSES = ['pending', 'confirmed', 'completed', 'no_show', 'cancelled'];
const PHOTO_KINDS = [
  { kind: 'customer', label: 'From the client' },
  { kind: 'before', label: 'Before' },
  { kind: 'after', label: 'After' }
];
const QUOTE_STATUS_LABEL = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' };
const EMPTY_FORM = { service_id: '', customer_id: '', date: '', time: '', end_time: '', status: 'confirmed', notes: '', admin_notes: '' };

/**
 * Create / edit an appointment. Crew (who's on the job) is chosen per
 * appointment and defaults to the job type's crew. `prefill` lets a quote
 * open this with the client, job type and quote already set.
 */
export default function AppointmentModal({ isOpen, onClose, onSave, appointment = null, defaultDate = '', prefill = null }) {
  const isEdit = !!appointment;
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [crew, setCrew] = useState([]); // staff ids on the job
  const [crewTouched, setCrewTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clientModal, setClientModal] = useState(null); // null | { client: object|null }
  const [images, setImages] = useState([]);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images: [{src,label}], index }
  const [quotes, setQuotes] = useState([]);
  const [quoteModal, setQuoteModal] = useState(null); // { quote } | { prefill }
  const [jobOptions, setJobOptions] = useState([]); // jobs this visit could move to
  const [ignoreHours, setIgnoreHours] = useState(false); // admin: book outside the roster
  const [endTouched, setEndTouched] = useState(false); // the end time follows the start until it is set by hand

  // Load reference data + prefill on open
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setCrewTouched(false);
    setEndTouched(false);
    setIgnoreHours(false);
    (async () => {
      const [s, sv, c] = await Promise.all([staffService.getAll(), servicesService.getAll(), clientsService.getAll()]);
      if (s.success) setStaff((s.staff || []).filter((p) => p.is_active));
      if (sv.success) setServices((sv.services || []).filter((x) => x.is_active));
      if (c.success) setClients(c.customers || []);
    })();

    if (appointment) {
      const { date, time } = utcToBrisbaneInputs(appointment.starts_at);
      setForm({
        service_id: String(appointment.service_id || ''),
        customer_id: String(appointment.customer_id || ''),
        date, time,
        end_time: appointment.ends_at ? utcToBrisbaneInputs(appointment.ends_at).time : '',
        status: appointment.status || 'confirmed',
        notes: appointment.notes || '',
        admin_notes: appointment.admin_notes || ''
      });
      setCrew(appointment.crew_ids?.length ? appointment.crew_ids : (appointment.staff_id ? [appointment.staff_id] : []));
    } else {
      setForm({
        ...EMPTY_FORM,
        service_id: prefill?.service_id ? String(prefill.service_id) : '',
        customer_id: prefill?.customer_id ? String(prefill.customer_id) : '',
        date: defaultDate || '',
        admin_notes: prefill?.admin_notes || ''
      });
      setCrew([]);
    }
  }, [isOpen, appointment, defaultDate, prefill]);

  // New appointment: the crew follows the job type until the user changes it.
  useEffect(() => {
    if (!isOpen || isEdit || crewTouched) return;
    const svc = services.find((s) => String(s.id) === form.service_id);
    if (svc) setCrew(svc.staff_ids || []);
  }, [isOpen, isEdit, crewTouched, services, form.service_id]);

  // The end time follows the start (keeping the visit's length: the job type's
  // duration for a new visit, the visit's own for an existing one) until set by hand.
  useEffect(() => {
    if (!isOpen || endTouched || !form.time) return;
    const svc = services.find((s) => String(s.id) === form.service_id);
    const minutes = isEdit ? (appointment?.duration_minutes || svc?.duration_minutes || DEFAULT_VISIT_MINUTES) : (svc?.duration_minutes || DEFAULT_VISIT_MINUTES);
    setForm((prev) => ({ ...prev, end_time: addMinutesToTime(prev.time, minutes) }));
  }, [isOpen, isEdit, endTouched, services, appointment, form.time, form.service_id]);

  // Quotes attached to this job (edit mode only).
  useEffect(() => {
    if (isOpen && appointment?.id) loadQuotes(appointment.id);
    else setQuotes([]);
  }, [isOpen, appointment]);
  async function loadQuotes(id) {
    const r = await quotesService.getAll({ appointmentId: id });
    if (r.success) setQuotes(r.quotes || []);
  }

  // The client's other jobs, for "Move to job…" on an existing visit.
  useEffect(() => {
    if (!isOpen || !appointment?.id) { setJobOptions([]); return; }
    jobsService.getAll(form.customer_id ? { customerId: form.customer_id } : {}).then((r) => { if (r.success) setJobOptions(r.jobs || []); });
  }, [isOpen, appointment?.id, form.customer_id]);
  async function moveToJob(jobId) {
    if (!jobId || !appointment?.id) return;
    const r = await jobsService.attachVisit(Number(jobId), appointment.id);
    if (r.success) { onSave?.(null); onClose(); } else setErrors({ submit: r.error || 'Could not move the visit' });
  }

  function change(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  }

  function toggleCrew(id) {
    setCrewTouched(true);
    setCrew((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    if (errors.crew) setErrors((prev) => ({ ...prev, crew: '' }));
  }

  // Photos — only for existing appointments (a new one has no id yet).
  useEffect(() => {
    if (isOpen && appointment?.id) {
      imagesService.forAppointment(appointment.id).then((r) => { if (r.success) setImages(r.images || []); });
    } else {
      setImages([]);
    }
  }, [isOpen, appointment]);

  async function uploadImages(kind, fileList) {
    if (!appointment?.id || !fileList?.length) return;
    setUploadingKind(kind);
    const r = await imagesService.upload(appointment.id, [...fileList], kind);
    setUploadingKind(null);
    if (r.success) setImages((prev) => [...prev, ...(r.images || [])]);
    else setErrors((prev) => ({ ...prev, submit: r.error || 'Photo upload failed' }));
  }

  async function removeImage(id) {
    if (!window.confirm('Delete this photo?')) return;
    const r = await imagesService.delete(id);
    if (r.success) setImages((prev) => prev.filter((i) => i.id !== id));
  }

  // Hard delete — for clearing test/noise bookings. Unlike cancelling,
  // no email goes to the client; photos and queued reminders go with it.
  async function removeAppointment() {
    if (!appointment?.id || deleting) return;
    if (!window.confirm('Permanently delete this appointment? No email is sent to the client, and its photos and reminders are removed too. This cannot be undone.')) return;
    setDeleting(true);
    const r = await appointmentsService.delete(appointment.id);
    setDeleting(false);
    if (r.success) {
      onSave?.(null);
      onClose();
    } else {
      setErrors({ submit: r.error || 'Failed to delete appointment' });
    }
  }

  function onClientSaved(client) {
    setClients((prev) => (prev.some((c) => c.id === client.id)
      ? prev.map((c) => (c.id === client.id ? client : c))
      : [...prev, client]));
    setForm((prev) => ({ ...prev, customer_id: String(client.id) }));
    setClientModal(null);
  }

  function validate() {
    const e = {};
    if (!form.service_id) e.service_id = 'Choose a job type';
    if (!crew.length) e.crew = 'Pick at least one person';
    if (!form.date) e.date = 'Pick a date';
    if (!form.time) e.time = 'Pick a time';
    if (form.time && form.end_time && minutesBetween(form.time, form.end_time) <= 0) e.end_time = 'After the start';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const startsAt = brisbaneInputsToUtc(form.date, form.time);
    const duration = minutesBetween(form.time, form.end_time) || null; // the visit's footprint; null = the job type's
    try {
      let response;
      if (isEdit) {
        // Move time / change the length if either changed, then update the other fields (+ crew if it changed).
        if (startsAt !== appointment.starts_at || (duration && duration !== appointment.duration_minutes)) {
          const r = await appointmentsService.reschedule(appointment.id, startsAt, ignoreHours, duration);
          if (!r.success) { setErrors({ submit: r.error || 'Could not reschedule' }); setSaving(false); return; }
        }
        const originalCrew = appointment.crew_ids?.length ? appointment.crew_ids : [appointment.staff_id];
        const crewChanged = crew.length !== originalCrew.length || crew.some((id) => !originalCrew.includes(id));
        response = await appointmentsService.update(appointment.id, {
          customer_id: form.customer_id ? Number(form.customer_id) : null,
          status: form.status,
          notes: form.notes,
          admin_notes: form.admin_notes,
          ...(crewChanged ? { staff_ids: crew, ignoreHours } : {})
        });
      } else {
        response = await appointmentsService.create({
          serviceId: Number(form.service_id),
          customerId: form.customer_id ? Number(form.customer_id) : null,
          startsAt,
          durationMinutes: duration,
          status: form.status,
          notes: form.notes,
          adminNotes: form.admin_notes,
          staffIds: crew,
          ...(prefill?.quote_id ? { quoteId: prefill.quote_id } : {}),
          ...(prefill?.job_id ? { jobId: prefill.job_id } : {}), // a visit added to an existing job
          ignoreHours
        });
      }
      if (response.success) {
        onSave?.(response.appointment);
        onClose();
      } else {
        setErrors({ submit: response.error || 'Failed to save appointment' });
      }
    } catch (err) {
      setErrors({ submit: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  const selectedService = services.find((s) => String(s.id) === form.service_id);
  const selectedClient = clients.find((c) => String(c.id) === form.customer_id);

  function newQuoteFromAppointment() {
    const svc = selectedService;
    const priced = svc && svc.price_type !== 'quote' && svc.price !== null && svc.price !== undefined && svc.price !== '';
    setQuoteModal({
      prefill: {
        customer_id: form.customer_id ? Number(form.customer_id) : null,
        appointment_id: appointment.id,
        title: svc?.name || '',
        items: svc ? [{ service_id: String(svc.id), description: svc.name, unit_price: priced ? svc.price : '' }] : []
      }
    });
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Appointment' : (prefill?.job_id ? 'Schedule a visit' : 'New Appointment')} size="large">
      <form onSubmit={submit} className="appt-form">
        <div className="form-group">
          <label htmlFor="service_id">Job type *</label>
          <select id="service_id" name="service_id" value={form.service_id} onChange={change}>
            <option value="">Select…</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name}{s.duration_minutes ? ` (${s.duration_minutes} min)` : ''}</option>)}
          </select>
          {errors.service_id && <span className="form-error">{errors.service_id}</span>}
        </div>

        <div className="form-group">
          <label>Who's on it *</label>
          <div className="crew-picker">
            {staff.map((p) => (
              <label key={p.id} className={`crew-option ${crew.includes(p.id) ? 'on' : ''}`}>
                <input type="checkbox" checked={crew.includes(p.id)} onChange={() => toggleCrew(p.id)} />
                <span className="crew-dot" style={{ background: p.color || 'var(--color-teal)' }} />
                {p.name}
              </label>
            ))}
          </div>
          {errors.crew && <span className="form-error">{errors.crew}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="customer_id">Client</label>
          <div className="inline-select-row">
            <select id="customer_id" name="customer_id" value={form.customer_id} onChange={change}>
              <option value="">No client / walk-in</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` — ${c.email}` : ''}</option>)}
            </select>
            {selectedClient && (
              <button type="button" className="btn btn-outline" onClick={() => setClientModal({ client: selectedClient })}>Edit</button>
            )}
            <button type="button" className="btn btn-outline" onClick={() => setClientModal({ client: null })}>+ New</button>
          </div>
          {selectedClient && (
            <div className="client-summary">
              {isEdit && Number(appointment?.prior_bookings) > 0 && <span className="chip chip-returning">Returning customer</span>}
              {selectedClient.phone && <span className="client-summary-item">📞 {selectedClient.phone}</span>}
              {selectedClient.address_street && (
                <span className="client-summary-item">
                  📍{' '}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([selectedClient.address_street, selectedClient.address_suburb, selectedClient.address_state, selectedClient.address_postcode].filter(Boolean).join(', '))}`}
                    target="_blank" rel="noopener noreferrer"
                  >
                    {[selectedClient.address_street, selectedClient.address_suburb, selectedClient.address_state, selectedClient.address_postcode].filter(Boolean).join(', ')} ↗
                  </a>
                </span>
              )}
              {selectedClient.notes && <span className="client-summary-item">📝 {selectedClient.notes}</span>}
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Date *</label>
            <input id="date" name="date" type="date" value={form.date} onChange={change} />
            {errors.date && <span className="form-error">{errors.date}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="time">Start *</label>
            <input id="time" name="time" type="time" value={form.time} onChange={change} />
            {errors.time && <span className="form-error">{errors.time}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="end_time">End{form.time && form.end_time && minutesBetween(form.time, form.end_time) > 0 ? ` · ${fmtHours(minutesBetween(form.time, form.end_time))}` : ''}</label>
            <input id="end_time" name="end_time" type="time" value={form.end_time} onChange={(e) => { setEndTouched(true); change(e); }} />
            {errors.end_time && <span className="form-error">{errors.end_time}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select id="status" name="status" value={form.status} onChange={change}>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>
        </div>

        <label className="checkbox-label appt-ignore-hours" title="Book even when the crew's roster (Availability) does not cover the time. Clashes with other visits still count.">
          <input type="checkbox" checked={ignoreHours} onChange={(e) => setIgnoreHours(e.target.checked)} /> Outside working hours is OK
        </label>

        {!isEdit && prefill?.job_id && (
          <p className="appt-hint text-muted">This visit will be added to the job.</p>
        )}

        {selectedService && (
          <p className="appt-hint text-muted">
            {selectedService.duration_minutes ? `${selectedService.duration_minutes} min · ` : ''}{selectedService.location_mode.replace('_', ' ')}
            {priceLabel(selectedService) ? ` · ${priceLabel(selectedService)}` : ''}
          </p>
        )}

        {isEdit && appointment?.job_number && (
          <p className="appt-hint text-muted">
            Part of job <Link to={`/jobs/${appointment.job_id}`} onClick={onClose}><strong>{appointment.job_number}</strong></Link>{appointment.job_title ? ` · ${appointment.job_title}` : ''}{appointment.job_status ? ` · ${String(appointment.job_status).replace('_', ' ')}` : ''}
            {jobOptions.some((j) => j.id !== appointment.job_id) && (
              <select aria-label="Move to job" className="appt-job-move" value="" onChange={(e) => moveToJob(e.target.value)} title="Move this visit (and its cost lines) onto another job">
                <option value="">Move to job…</option>
                {jobOptions.filter((j) => j.id !== appointment.job_id).map((j) => <option key={j.id} value={j.id}>{j.job_number}{j.title ? ` · ${j.title}` : ''}{j.customer_name ? ` · ${j.customer_name}` : ''}</option>)}
              </select>
            )}
          </p>
        )}

        {isEdit && appointment?.quote_number && (
          <div className="appt-quote-row mb-md">
            <span>Booked from quote <strong>{appointment.quote_number}</strong> · {formatMoney(appointment.quote_total)}</span>
            <span className="spacer" />
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setQuoteModal({ quote: { id: appointment.quote_id } })}>Open</button>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="notes">Notes (visible to client)</label>
          <textarea id="notes" name="notes" rows="2" value={form.notes} onChange={change} />
        </div>
        <div className="form-group">
          <label htmlFor="admin_notes">Private notes</label>
          <textarea id="admin_notes" name="admin_notes" rows="2" value={form.admin_notes} onChange={change} />
        </div>

        {isEdit && (
          <div className="form-group">
            <div className="appt-quotes-head">
              <label>Quotes</label>
              <button type="button" className="btn btn-sm btn-outline" onClick={newQuoteFromAppointment}>+ New quote</button>
            </div>
            {quotes.length === 0 ? (
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>No quotes for this job yet.</p>
            ) : (
              <div className="appt-quotes">
                {quotes.map((q) => (
                  <div key={q.id} className="appt-quote-row">
                    <strong>{q.quote_number}</strong>
                    <span className={`status-badge status-${q.effective_status}`}>{QUOTE_STATUS_LABEL[q.effective_status] || q.effective_status}</span>
                    <span>{formatMoney(q.total)}</span>
                    <span className="spacer" />
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => setQuoteModal({ quote: q })}>Open</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {isEdit && (
          <JobCosts appointment={appointment} jobId={appointment.job_id || null} staff={staff} onLightbox={(imgs, index) => setLightbox({ images: imgs, index })} />
        )}

        {isEdit && (
          <div className="form-group appt-photos">
            <label>Photos</label>
            {PHOTO_KINDS.map(({ kind, label }) => {
              const ofKind = images.filter((i) => i.kind === kind);
              return (
                <div key={kind} className="photo-kind-row">
                  <div className="photo-kind-head">
                    <span className="photo-kind-label">{label}{ofKind.length ? ` (${ofKind.length})` : ''}</span>
                    <label className={`btn btn-sm btn-outline photo-add ${uploadingKind === kind ? 'disabled' : ''}`}>
                      {uploadingKind === kind ? 'Uploading…' : '+ Add'}
                      <input type="file" accept="image/*" multiple hidden disabled={uploadingKind === kind}
                        onChange={(e) => { uploadImages(kind, e.target.files); e.target.value = ''; }} />
                    </label>
                  </div>
                  {ofKind.length > 0 && (
                    <div className="appt-photo-thumbs">
                      {ofKind.map((img, i) => (
                        <div key={img.id} className="appt-photo-thumb">
                          <button type="button" className="appt-photo-open" title={img.original_name || label}
                            onClick={() => setLightbox({
                              images: ofKind.map((im) => ({ src: imagesService.fileUrl(im.id), label: `${label}${im.original_name ? ` — ${im.original_name}` : ''}` })),
                              index: i
                            })}>
                            <img src={imagesService.fileUrl(img.id, 'thumb')} alt={img.original_name || label} />
                          </button>
                          <button type="button" className="appt-photo-remove" aria-label="Delete photo" onClick={() => removeImage(img.id)}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {errors.submit && <div className="form-error text-center mb-md">{errors.submit}</div>}

        <div className="modal-actions">
          {isEdit && (
            <button type="button" className="btn btn-danger appt-delete" onClick={removeAppointment} disabled={deleting || saving}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || deleting}>
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Appointment')}
          </button>
        </div>
      </form>

      <ClientModal isOpen={!!clientModal} onClose={() => setClientModal(null)} onSave={onClientSaved} client={clientModal?.client || null} />
      {lightbox && <Lightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
      <QuoteModal
        isOpen={!!quoteModal}
        onClose={() => { setQuoteModal(null); if (appointment?.id) loadQuotes(appointment.id); }}
        onSave={() => {}}
        quote={quoteModal?.quote || null}
        prefill={quoteModal?.prefill || null}
      />
    </Modal>
  );
}
